/* PetCorner V3.11.13 — stable premium native filters.
   Search & Discovery is the source of truth.
   Desktop applies immediately by URL navigation.
   Mobile stages choices and applies them only on CTA. A lightweight section
   request may preview the exact result count; it never replaces the product grid. */
(() => {
  const S = {
    desktopFilterForm: '#FacetFiltersForm',
    desktopSortForm: '#FacetSortForm',
    mobileFilterForm: '#FacetFiltersFormMobile',
    mobileSortSelect: '.pc-mobile-sort-trigger__select',
    mobileApply: '[data-pc-apply-filters]',
    clearDraft: '[data-pc-clear-draft]',
    selectionSummary: '[data-pc-selection-summary]'
  };

  const isSearchPage = Boolean(document.querySelector('.template-search.pc-collection-commerce'));

  // The Search template still loads Dawn facets.js for its desktop horizontal filters.
  // On mobile we stage choices in the PetCorner drawer, so block Dawn's immediate
  // input-driven AJAX only inside the two mobile forms.
  const filterFacetSearchItems = (input) => {
    if (!(input instanceof HTMLInputElement)) return;
    const query = input.value.trim().toLocaleLowerCase('sr');
    const facet = input.closest('.pc-mobile-inline-facet');
    facet?.querySelectorAll('[data-pc-search-item]').forEach((item) => {
      const label = item.querySelector('.facet-checkbox__text-label')?.textContent?.trim().toLocaleLowerCase('sr') || '';
      const show = !query || label.includes(query);
      item.classList.toggle('pc-search-visible', Boolean(query && show));
      item.hidden = !show;
    });
  };

  const bindSearchMobileInterception = () => {
    if (!isSearchPage) return;
    ['#FacetFiltersFormMobile', '#FacetSortMobileToolbarForm'].forEach((selector) => {
      const form = document.querySelector(selector);
      if (!form || form.dataset.pcSearchInputIntercept) return;
      form.dataset.pcSearchInputIntercept = 'true';
      form.addEventListener('input', (event) => {
        if (!window.matchMedia('(max-width: 749px)').matches) return;

        // The small search box inside Brand is a LOCAL list search. Dawn's mobile
        // facets listener must not consume it, but it still needs to filter the
        // visible brand items immediately. Handle it here in capture phase, then
        // stop the event before Dawn can treat it as a storefront filter change.
        const target = event.target;
        if (target instanceof HTMLInputElement && target.matches('[data-pc-facet-search]')) {
          filterFacetSearchItems(target);
          event.stopImmediatePropagation();
          return;
        }

        event.stopImmediatePropagation();
      }, true);
    });
  };

  const formParams = (form) => form ? new URLSearchParams(new FormData(form)) : new URLSearchParams();
  const mergeParams = (...sets) => {
    const out = new URLSearchParams();
    sets.forEach((set) => set && [...set.entries()].forEach(([key, value]) => out.append(key, value)));
    return out;
  };
  const navigate = (params) => {
    const query = params.toString();
    document.documentElement.classList.add('pc-filter-navigating');
    window.location.assign(`${window.location.pathname}${query ? `?${query}` : ''}`);
  };

  const getDesktopParams = () => mergeParams(
    formParams(document.querySelector(S.desktopFilterForm)),
    formParams(document.querySelector(S.desktopSortForm))
  );
  const getMobileParams = () => {
    const params = formParams(document.querySelector(S.mobileFilterForm));
    const sort = document.querySelector(S.mobileSortSelect);
    if (sort?.value) params.set('sort_by', sort.value);
    return params;
  };

  const selectionText = (count) => {
    if (count === 1) return '1 filter aktivan';
    if (count >= 2 && count <= 4) return `${count} filtera aktivna`;
    return `${count} filtera aktivno`;
  };
  const productText = (count) => count === 1 ? 'Prikaži 1 proizvod' : `Prikaži ${count} proizvoda`;

  let desktopTimer;
  const bindDesktop = () => {
    const form = document.querySelector(S.desktopFilterForm);
    if (form && !form.dataset.pcBound) {
      form.dataset.pcBound = 'true';
      form.addEventListener('change', (event) => {
        const target = event.target;
        if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
        clearTimeout(desktopTimer);
        const delay = (target.type === 'text' || target.type === 'number') ? 420 : 120;
        desktopTimer = setTimeout(() => navigate(getDesktopParams()), delay);
      });
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        navigate(getDesktopParams());
      });
    }
    const sort = document.querySelector(`${S.desktopSortForm} select[name="sort_by"]`);
    if (sort && !sort.dataset.pcBound) {
      sort.dataset.pcBound = 'true';
      sort.addEventListener('change', () => navigate(getDesktopParams()));
    }
  };

  const getMobileSelections = () => {
    const form = document.querySelector(S.mobileFilterForm);
    if (!form) return [];
    const selections = [...form.querySelectorAll('input[type="checkbox"]:checked:not(:disabled)')];
    const min = form.querySelector('.pc-price-range__input[name*="price.gte"]')?.value?.trim();
    const max = form.querySelector('.pc-price-range__input[name*="price.lte"]')?.value?.trim();
    if (min || max) selections.push({ pcPriceSelection: true });
    return selections;
  };

  const updateToolbarBadge = (count) => {
    const label = document.querySelector('.mobile-facets__open-label.medium-hide.large-up-hide');
    if (!label) return;
    let badge = label.querySelector('.pc-mobile-filter-badge');
    if (count > 0) {
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'pc-mobile-filter-badge';
        label.appendChild(badge);
      }
      badge.textContent = count;
      badge.hidden = false;
    } else if (badge) {
      badge.hidden = true;
    }
  };

  const updatePanelSelectedLabels = () => {
    const form = document.querySelector(S.mobileFilterForm);
    if (!form) return;
    form.querySelectorAll('details.pc-mobile-inline-facet').forEach((panel) => {
      let count = panel.querySelectorAll('input[type="checkbox"]:checked:not(:disabled)').length;
      if (panel.classList.contains('pc-mobile-price-facet')) {
        const hasPrice = [...panel.querySelectorAll('.pc-price-range__input')].some((input) => input.value.trim());
        count = hasPrice ? 1 : 0;
      }
      const title = panel.querySelector('.pc-mobile-inline-facet__title');
      let selected = panel.querySelector('.pc-mobile-inline-facet__selected');
      if (count > 0 && title) {
        if (!selected) {
          selected = document.createElement('span');
          selected.className = 'pc-mobile-inline-facet__selected';
          title.insertAdjacentElement('afterend', selected);
        }
        selected.textContent = count === 1 ? '1 izbor' : `${count} izbora`;
      } else {
        selected?.remove();
      }
      panel.classList.toggle('pc-has-active', count > 0);
    });
  };

  let previewTimer;
  let previewController;
  const setPreviewCount = (count) => {
    if (!Number.isFinite(count)) return;
    const form = document.querySelector(S.mobileFilterForm);
    const apply = document.querySelector(S.mobileApply);
    const countNode = form?.querySelector('.mobile-facets__count');
    if (apply) {
      const label = apply.querySelector('.pc-mobile-apply-label');
      if (label) label.textContent = productText(count);
      apply.classList.remove('is-previewing');
      apply.removeAttribute('aria-busy');
    }
    if (countNode) countNode.textContent = count === 1 ? '1 proizvod' : `${count} proizvoda`;
  };

  const requestPreviewCount = () => {
    const form = document.querySelector(S.mobileFilterForm);
    if (!form) return;
    const sectionId = form.dataset.pcSectionId;
    if (!sectionId) return;
    clearTimeout(previewTimer);
    previewTimer = setTimeout(async () => {
      previewController?.abort();
      previewController = new AbortController();
      const apply = document.querySelector(S.mobileApply);
      apply?.classList.add('is-previewing');
      apply?.setAttribute('aria-busy', 'true');
      try {
        const params = getMobileParams();
        params.set('section_id', sectionId);
        const response = await fetch(`${window.location.pathname}?${params.toString()}`, {
          credentials: 'same-origin',
          signal: previewController.signal,
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const root = doc.querySelector('.pc-collection-commerce[data-pc-results-count]');
        const rawCount = root?.dataset.pcResultsCount;
        let count = rawCount === undefined || rawCount === null || rawCount === '' ? NaN : Number(rawCount);
        if (!Number.isFinite(count)) {
          const countText = doc.querySelector('#FacetFiltersFormMobile .mobile-facets__count')?.textContent || '';
          const match = countText.replace(/\./g, '').match(/\d+/);
          count = match ? Number(match[0]) : NaN;
        }
        if (Number.isFinite(count)) setPreviewCount(count);
        else throw new Error('Missing result count');
      } catch (error) {
        if (error?.name !== 'AbortError') {
          apply?.classList.remove('is-previewing');
          apply?.removeAttribute('aria-busy');
          const label = apply?.querySelector('.pc-mobile-apply-label');
          if (label) label.textContent = 'Prikaži proizvode';
        }
      }
    }, 260);
  };

  const renderDraftState = ({ preview = false } = {}) => {
    const selections = getMobileSelections();
    const clear = document.querySelector(S.clearDraft);
    const summary = document.querySelector(S.selectionSummary);
    const count = selections.length;
    if (summary) {
      summary.textContent = selectionText(count);
      summary.hidden = count === 0;
    }
    if (clear) clear.hidden = count === 0;
    updateToolbarBadge(count);
    updatePanelSelectedLabels();
    document.querySelectorAll(`${S.mobileFilterForm} .mobile-facets__label`).forEach((label) => {
      const input = label.querySelector('input[type="checkbox"]');
      label.classList.toggle('active', Boolean(input?.checked));
    });
    if (preview) requestPreviewCount();
  };

  const clearDraft = () => {
    const form = document.querySelector(S.mobileFilterForm);
    if (!form) return;
    form.querySelectorAll('input[type="checkbox"]:checked').forEach((input) => { input.checked = false; });
    form.querySelectorAll('.pc-price-range__input').forEach((input) => { input.value = ''; });
    renderDraftState({ preview: true });
  };

  const bindFacetMore = () => {
    document.querySelectorAll('[data-pc-facet-more]').forEach((button) => {
      if (button.dataset.pcBound) return;
      button.dataset.pcBound = 'true';
      button.dataset.pcCollapsedText = button.textContent.trim();
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const facet = button.closest('.pc-mobile-inline-facet, .pc-mobile-static-facet');
        if (!facet) return;
        const expanded = facet.classList.toggle('pc-is-expanded');
        button.textContent = expanded ? 'Prikaži manje' : button.dataset.pcCollapsedText;
      });
    });
  };

  const bindFacetSearch = () => {
    document.querySelectorAll('[data-pc-facet-search]').forEach((input) => {
      if (input.dataset.pcBound) return;
      input.dataset.pcBound = 'true';
      input.addEventListener('input', () => filterFacetSearchItems(input));
      input.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          event.stopPropagation();
        }
      });
      input.addEventListener('search', () => filterFacetSearchItems(input));
    });
  };

  const bindPremiumAccordions = () => {
    const mobileForm = document.querySelector(S.mobileFilterForm);
    if (mobileForm && !mobileForm.dataset.pcAccordionInit) {
      mobileForm.dataset.pcAccordionInit = 'true';
      const details = [...mobileForm.querySelectorAll('details.pc-mobile-inline-facet')];
      const activePanel = details.find((panel) => panel.classList.contains('pc-has-active'));
      details.forEach((panel) => { panel.open = panel === activePanel; });
      details.forEach((panel) => panel.addEventListener('toggle', () => {
        if (!panel.open) return;
        details.forEach((other) => { if (other !== panel && other.open) other.open = false; });
      }));
    }

    const desktopForm = document.querySelector(S.desktopFilterForm);
    if (desktopForm && !desktopForm.dataset.pcAccordionInit) {
      desktopForm.dataset.pcAccordionInit = 'true';
      const details = [...desktopForm.querySelectorAll(':scope > details.facets__disclosure-vertical')];
      details.forEach((panel) => panel.addEventListener('toggle', () => {
        if (!panel.open) return;
        details.forEach((other) => { if (other !== panel && other.open) other.open = false; });
      }));
    }
  };

  let mobileDraftSnapshot = null;
  let mobileApplying = false;

  const captureMobileDraft = (form) => {
    if (!form) return null;
    return [...form.querySelectorAll('input')].map((input) => ({
      input,
      checked: input.checked,
      value: input.value
    }));
  };

  const restoreMobileDraft = () => {
    if (!mobileDraftSnapshot) return;
    mobileDraftSnapshot.forEach(({ input, checked, value }) => {
      if (!input?.isConnected) return;
      if (input.type === 'checkbox' || input.type === 'radio') input.checked = checked;
      else input.value = value;
    });
    const form = document.querySelector(S.mobileFilterForm);
    const initialCount = Number(form?.dataset.pcResultsCount);
    if (Number.isFinite(initialCount)) setPreviewCount(initialCount);
    renderDraftState();
  };


  const bindHeaderClose = () => {
    document.querySelectorAll('[data-pc-close-mobile-filter]').forEach((button) => {
      if (button.dataset.pcBound) return;
      button.dataset.pcBound = 'true';
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const disclosure = button.closest('details.mobile-facets__disclosure');
        const summary = disclosure?.querySelector(':scope > summary');
        summary?.click();
      });
    });
  };

  const bindDrawerCancelBehavior = () => {
    const disclosure = document.querySelector('.mobile-facets__disclosure');
    const form = document.querySelector(S.mobileFilterForm);
    if (!disclosure || !form || disclosure.dataset.pcDraftBound) return;
    disclosure.dataset.pcDraftBound = 'true';
    disclosure.addEventListener('toggle', () => {
      if (disclosure.open) {
        mobileApplying = false;
        mobileDraftSnapshot = captureMobileDraft(form);
      } else if (!mobileApplying) {
        restoreMobileDraft();
      }
    });
  };

  const bindMobile = () => {
    const form = document.querySelector(S.mobileFilterForm);
    const apply = document.querySelector(S.mobileApply);
    if (form && !form.dataset.pcBound) {
      form.dataset.pcBound = 'true';
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        mobileApplying = true;
        navigate(getMobileParams());
      });
      form.addEventListener('change', (event) => {
        if (event.target instanceof HTMLInputElement) renderDraftState({ preview: true });
      });
      form.addEventListener('input', (event) => {
        if (event.target instanceof HTMLInputElement && event.target.classList.contains('pc-price-range__input')) renderDraftState({ preview: true });
      });
    }
    if (apply && !apply.dataset.pcBound) {
      apply.dataset.pcBound = 'true';
      apply.addEventListener('click', () => { mobileApplying = true; navigate(getMobileParams()); });
    }
    const clear = document.querySelector(S.clearDraft);
    if (clear && !clear.dataset.pcBound) {
      clear.dataset.pcBound = 'true';
      clear.addEventListener('click', clearDraft);
    }
    const sort = document.querySelector(S.mobileSortSelect);
    if (sort && !sort.dataset.pcBound) {
      sort.dataset.pcBound = 'true';
      sort.addEventListener('change', () => {
        const params = new URLSearchParams(window.location.search);
        if (sort.value) params.set('sort_by', sort.value);
        else params.delete('sort_by');
        navigate(params);
      });
    }

    const initialCount = Number(form?.dataset.pcResultsCount);
    if (Number.isFinite(initialCount)) setPreviewCount(initialCount);
    renderDraftState();
  };

  const init = () => {
    bindSearchMobileInterception();
    // Search keeps Dawn's stable desktop horizontal filtering. Collection pages
    // continue using PetCorner's desktop vertical navigation.
    if (!isSearchPage) bindDesktop();
    bindMobile();
    bindFacetMore();
    bindFacetSearch();
    bindPremiumAccordions();
    bindDrawerCancelBehavior();
    bindHeaderClose();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  document.addEventListener('shopify:section:load', init);
})();
